import React from 'react';
import { ChevronRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const StepIndicatorStyles = () => (
  <style>{`
    .step-indicator {
      background: #fff;
      padding: clamp(12px, 2vw, 20px);
      border-radius: 4px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.08);
      border-bottom: 1px solid #e5e5e5;
      max-width: 1200px;
      margin: 0 6px clamp(16px, 3vw, 28px) 6px;
      box-sizing: border-box;
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
    }

    .step-container {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: clamp(15px, 3vw, 30px);
      flex-wrap: wrap;
    }

    .step {
      display: flex;
      align-items: center;
      gap: clamp(8px, 2vw, 12px);
      color: #999;
      font-weight: 400;
      font-size: clamp(0.8rem, 2vw, 0.95rem);
      white-space: nowrap;
    }

    .step-active {
      color: #2F7D5E;
      font-weight: 400;
    }

    .step-completed {
      color: #2F7D5E;
      font-weight: 400;
    }

    .step-number {
      width: clamp(32px, 6vw, 36px);
      height: clamp(32px, 6vw, 36px);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f0f0f0;
      color: #999;
      font-weight: 400;
      font-size: clamp(0.8rem, 2vw, 0.9rem);
      transition: all 0.3s ease;
      flex-shrink: 0;
    }

    .step-number-active {
      background: #2F7D5E;
      color: #fff;
    }

    .step-number-completed {
      background: #2F7D5E;
      color: #fff;
    }

    .step-arrow {
      color: #ccc;
      flex-shrink: 0;
      width: clamp(16px, 3vw, 20px);
      height: clamp(16px, 3vw, 20px);
    }

    /* 手機優化 - 隱藏文字,只顯示數字 */
    @media (max-width: 640px) {
      .step-container {
        gap: 10px;
      }

      .step-label {
        display: none;
      }

      .step {
        gap: 0;
      }

      .step-arrow {
        margin: 0 5px;
      }
    }

    /* 超小手機 - 更緊湊 */
    @media (max-width: 375px) {
      .step-indicator {
        padding: 12px;
        margin-bottom: 15px;
      }

      .step-container {
        gap: 8px;
      }

      .step-number {
        width: 28px;
        height: 28px;
        font-size: 0.75rem;
      }

      .step-arrow {
        width: 14px;
        height: 14px;
        margin: 0 3px;
      }
    }

    /* 平板 - 顯示簡短文字 */
    @media (min-width: 641px) and (max-width: 768px) {
      .step-label-full {
        display: none;
      }

      .step-label-short {
        display: inline;
      }
    }

    /* 桌面 - 顯示完整文字 */
    @media (min-width: 769px) {
      .step-label-full {
        display: inline;
      }

      .step-label-short {
        display: none;
      }
    }

    /* 橫向模式優化 */
    @media (max-height: 600px) and (orientation: landscape) {
      .step-indicator {
        padding: 10px;
        margin-bottom: 15px;
      }

      .step-container {
        gap: 12px;
      }
    }

    /* 減少動畫 */
    @media (prefers-reduced-motion: reduce) {
      .step-number {
        transition: none;
      }
    }

    /* 深色模式 */
    @media (prefers-color-scheme: dark) {
      .step-indicator {
        background: #1a1a1a;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        border-bottom-color: #333;
      }

      .step {
        color: #666;
      }

      .step-active {
        color: #2F7D5E;
      }

      .step-completed {
        color: #2F7D5E;
      }

      .step-number {
        background: #333;
        color: #777;
      }

      .step-number-active {
        background: #2F7D5E;
        color: #fff;
      }

      .step-number-completed {
        background: #2F7D5E;
        color: #fff;
      }

      .step-arrow {
        color: #444;
      }
    }
  `}</style>
);

const StepIndicator = ({ currentStep, bookingMode }) => {
  const { t } = useTranslation('booking');

  let steps = [];

  if (bookingMode === 'single') {
    steps = [
      { number: 1, label: t('steps.single_menu', '選餐'), shortLabel: t('steps.single_menu_short', '選餐') },
      { number: 2, label: t('steps.single_confirm', '確認付款'), shortLabel: t('steps.single_confirm_short', '付款') }
    ];
  } else {
    steps = [
      { number: 1, label: t('steps.date'), shortLabel: t('steps.date_short') },
      { number: 2, label: t('steps.meals'), shortLabel: t('steps.meals_short') },
      { number: 3, label: t('steps.time'), shortLabel: t('steps.time_short') },
      { number: 4, label: t('steps.payment'), shortLabel: t('steps.payment_short') }
    ];
  }

  return (
    <>
      <StepIndicatorStyles />
      <div className="step-indicator">
        <div className="step-container">
          {steps.map((step, index) => {
            const isActive = currentStep === step.number;
            const isCompleted = currentStep > step.number;
            return (
              <React.Fragment key={step.number}>
                <div className={`step ${isActive ? 'step-active' : ''} ${isCompleted ? 'step-completed' : ''}`}>
                  <div className={`step-number ${isActive ? 'step-number-active' : ''} ${isCompleted ? 'step-number-completed' : ''}`}>
                    {isCompleted ? <Check size={16} /> : step.number}
                  </div>
                  <span className="step-label">
                    <span className="step-label-full">{step.label}</span>
                    <span className="step-label-short">{step.shortLabel}</span>
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <ChevronRight className="step-arrow" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default StepIndicator;
